import { Component, Input, OnInit } from '@angular/core';

/**
 * Available icon size variants.
 *
 * @remarks
 * Size values map to predefined icon dimensions in the Backbase theme.
 * The `md` size is the default when no size is specified.
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="star" size="sm"></bb-icon-ui>
 * ```
 */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

/**
 * Displays an icon with configurable size, color, and appearance options.
 *
 * @remarks
 * Renders scalable icons from the Backbase icon library with support for sizing, colors,
 * animation, RTL flipping, and accessibility.
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="star" size="md"></bb-icon-ui>
 * ```
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="visibility" aria-label="Show password"></bb-icon-ui>
 * ```
 *
 * @example
 *
 * ```html
 * <bb-icon-ui name="error" size="sm" color="light"></bb-icon-ui>
 * ```
 */
@Component({
  selector: 'bb-icon-ui',
  templateUrl: './icon.component.html',
  standalone: false,
})
export class IconComponent implements OnInit {
  private _cropped = false;
  private _flipOnRtl = false;

  /**
   * The name of the icon from the Backbase icon library.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star"></bb-icon-ui>
   * ```
   *
   * @example
   *
   * ```html
   * <bb-icon-ui [name]="paginationIcons.first"></bb-icon-ui>
   * ```
   */
  @Input() name: string | undefined;

  /**
   * Whether to use white background and inverse icon colors.
   *
   * @deprecated Deprecated in ui-ang@14.0.0. To be removed in ui-ang@16.0.0. Use `bb-emblem-ui` instead.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star" [inverse]="true"></bb-icon-ui>
   * ```
   */
  @Input() inverse = false;

  /**
   * The size of the icon represented as a size name contained in backbase-theme. This is ignored
   * when the circle option is used. Defaults to `md`.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="error" size="sm"></bb-icon-ui>
   * ```
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star" size="xxl"></bb-icon-ui>
   * ```
   */
  @Input() size: IconSize = 'md';

  /**
   * The color of the icon represented as a color name contained in backbase-theme.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star" color="light"></bb-icon-ui>
   * ```
   */
  @Input() color: string | undefined;

  /**
   * Whether the icon should be animated. Defaults to `false`.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="cached" size="lg" [animate]="animate"></bb-icon-ui>
   * ```
   */
  @Input() animate = false;

  /**
   * Input that can be used to specify the `aria-label` attribute for accessibility.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="visibility" aria-label="Show password"></bb-icon-ui>
   * ```
   */
  @Input('aria-label') ariaLabel: string | undefined;

  /**
   * Whether the icon should be cropped horizontally.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="success" size="md" color="primary" cropped></bb-icon-ui>
   * ```
   */
  @Input()
  get cropped(): boolean | string {
    return this._cropped;
  }

  /**
   * Sets whether the icon should be cropped horizontally.
   *
   * @param value - Boolean or string value (string "false" treated as false)
   *
   * @example
   *
   * ```typescript
   * iconComponent.cropped = true;
   * ```
   */
  set cropped(value: boolean | string) {
    this._cropped = value != null && `${value}` !== 'false';
  }

  /**
   * Whether the icon should be flipped when the page direction is RTL (right-to-left).
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="arrow-back" size="md" flipOnRtl="true" cropped></bb-icon-ui>
   * ```
   */
  @Input() set flipOnRtl(value: boolean) {
    this._flipOnRtl = value;
  }

  /**
   * Gets whether the icon should be flipped in RTL mode.
   *
   * @returns true if icon flips in RTL mode
   *
   * @example
   *
   * ```typescript
   * if (iconComponent.flipOnRtl) {
   *   // Icon will flip when page direction is RTL
   * }
   * ```
   */
  get flipOnRtl(): boolean {
    return this._flipOnRtl;
  }

  /**
   * Whether the icon has a background. If not set it will display just the icon without background.
   * Possible values are `circle` and `square`.
   *
   * @deprecated Deprecated in ui-ang@14.0.0. To be removed in ui-ang@16.0.0. There is no replacement; please consider using `bb-emblem-ui`.
   *
   * @example
   *
   * ```html
   * <bb-icon-ui name="star" backgroundType="circle"></bb-icon-ui>
   * ```
   */
  @Input() backgroundType: 'circle' | 'square' | undefined;

  /**
   * Computes CSS class names based on icon configuration.
   *
   * @remarks
   * This getter dynamically generates the list of CSS classes applied to the icon element
   * based on the configured properties like name, size, color, animation state, and background type.
   * The classes are filtered to remove any falsy values before being applied.
   *
   * @returns Array of CSS class names to apply to the icon element
   *
   * @example
   *
   * ```html
   * <span [ngClass]="classNames" class="bb-icon"></span>
   * ```
   */
  get classNames(): string[] {
    return [
      this.name ? `bb-icon-${this.name}` : '',
      this.animate ? 'bb-icon--animate' : '',
      this.inverse ? 'bb-icon--inverse' : '',
      this.flipOnRtl ? 'bb-icon--rtl' : '',
      this.size ? `bb-icon--${this.size}` : 'bb-icon--md',
      this.backgroundType === 'circle' ? 'icon-circle' : '',
      this._cropped ? 'bb-icon--cropped' : '',
      ...this.getBackgroundTypeClass(),
    ].filter(Boolean);
  }

  /**
   * Validates that the required name input is provided.
   *
   * @remarks
   * Throws an error if the name input is not provided, as the icon cannot render without a name.
   * This ensures early detection of configuration errors.
   */
  ngOnInit() {
    if (typeof this.name === 'undefined') {
      throw new Error(`"name" input is required in "${this.constructor.name}"`);
    }
  }

  private getBackgroundTypeClass() {
    if (this.backgroundType) {
      return [
        'bb-icon__background',
        `bb-icon__background--${this.backgroundType}`,
        this.color ? `bb-icon__background--${this.color}` : 'bb-icon__background--primary',
      ];
    } else {
      return [this.color ? `bb-icon--${this.color}` : ''];
    }
  }
}
